// components/Breadcrumb/breadcrumbMap.ts
// Breadcrumb structure with proper hierarchy: Section > Subsection > Page

import { FaExclamation, FaUsers } from "react-icons/fa";
import { BiSolidOffer } from "react-icons/bi";
import { BsBarChartLine, BsLayersFill } from "react-icons/bs";
import { CiGlobe, CiSettings } from "react-icons/ci";
import { IoIosHelpCircleOutline } from "react-icons/io";
import { LuWalletCards } from "react-icons/lu";
import { MdDiscount, MdOutlineDashboard, MdOutlineFeedback, MdOutlineProductionQuantityLimits } from "react-icons/md";
import { RiAdminFill, RiNotification2Line, RiUserShared2Fill } from "react-icons/ri";

import { VscRequestChanges } from "react-icons/vsc";

export interface BreadcrumbItem {
    label: string;
    path: string;
    icon: React.ElementType;
}

/**
 * BREADCRUMB_MAP with hierarchical structure
 * 
 * Format: Section > Subsection > Current Page
 * 
 * Examples:
 * - Main > Dashboard
 * - Main > Analytics
 * - User Accounts > Users
 * - User Accounts > Business
 * - User Accounts > Drivers
 * - Operations > Orders
 * - Support > Help desk
 * - Management > Promo codes
 */

export const BREADCRUMB_MAP: Record<string, BreadcrumbItem[]> = {
    // ========================
    // MAIN SECTION
    // ========================

    // Main page itself
    "/dashboard": [
        { label: "Main", path: "/dashboard", icon: MdOutlineDashboard },
        { label: "Dashboard", path: "/dashboard", icon: MdOutlineDashboard }
    ],

    // Main > Analytics
    "/dashboard/analytics": [
        { label: "Main", path: "/dashboard", icon: MdOutlineDashboard },
        { label: "Analytics", path: "/dashboard/analytics", icon: BsBarChartLine },
    ],

    // ========================
    // USER ACCOUNTS SECTION
    // ========================

    // User Accounts section page itself (if you have one)
    // "/dashboard/user-accounts": [
    //   { label: "User Accounts", path: "/dashboard/user-accounts", icon: FaUsers },
    // ],

    // User Accounts > Users
    "/dashboard/users": [
        { label: "User accounts", path: "/dashboard/users", icon: FaUsers },
        { label: "Users", path: "/dashboard/users", icon: FaUsers },
    ],

    // User Accounts > Users > User Details (if you add this route)
    "/dashboard/users/:id": [
        { label: "User accounts", path: "/dashboard/users", icon: FaUsers },
        { label: "Users", path: "/dashboard/users", icon: FaUsers },
        { label: "User details", path: "/dashboard/users/:id", icon: FaUsers },
    ],

    // User Accounts > Business
    "/dashboard/business": [
        { label: "User accounts", path: "/dashboard/users", icon: FaUsers },
        { label: "Business", path: "/dashboard/business", icon: FaUsers },
    ],

    // User Accounts > Drivers
    "/dashboard/drivers": [
        { label: "User accounts", path: "/dashboard/users", icon: FaUsers },
        { label: "Drivers", path: "/dashboard/drivers", icon: RiUserShared2Fill },
    ],

    // ========================
    // OPERATIONS SECTION
    // ========================

    // Operations > Orders
    "/dashboard/orders": [
        { label: "Operations", path: "/dashboard/orders", icon: MdOutlineProductionQuantityLimits },
        { label: "Orders", path: "/dashboard/orders", icon: MdOutlineProductionQuantityLimits },
    ],

    // Operations > Orders > Order Details (if you add this route)
    "/dashboard/orders/:id": [
        { label: "Operations", path: "/dashboard/orders", icon: MdOutlineProductionQuantityLimits },
        { label: "Orders", path: "/dashboard/orders", icon: MdOutlineProductionQuantityLimits },
        { label: "Order details", path: "/dashboard/orders/:id", icon: MdOutlineProductionQuantityLimits },
    ],
    //Operations > Batches
    "/dashboard/batches": [
        { label: "Operations", path: "/dashboard/batches", icon: MdOutlineProductionQuantityLimits },
        { label: "Batches", path: "/dashboard/batches", icon: BsLayersFill },
    ],
    "/dashboard/batches/:id": [
        { label: "Operations", path: "/dashboard/batches", icon: MdOutlineProductionQuantityLimits },
        { label: "Batches", path: "/dashboard/batches", icon: MdOutlineProductionQuantityLimits },
        { label: "Batch details", path: "/dashboard/batches/:id", icon: MdOutlineProductionQuantityLimits },
    ],
    // Operations > Driver documentation update requests
    "/dashboard/driver-documentation-update-requests": [
        { label: "Operations", path: "/dashboard/driver-documentation-update-requests", icon: MdOutlineProductionQuantityLimits },
        { label: "Driver documentation update requests", path: "/dashboard/driver-documentation-update-requests", icon: VscRequestChanges },
    ],

    // Operations > Feedback
    "/dashboard/feedback": [
        { label: "Operations", path: "/dashboard/feedback", icon: MdOutlineProductionQuantityLimits },
        { label: "Feedback", path: "/dashboard/feedback", icon: MdOutlineFeedback },
    ],

    // Operations > Announcements
    "/dashboard/announcements": [
        { label: "Operations", path: "/dashboard/announcements", icon: MdOutlineProductionQuantityLimits },
        { label: "Announcements", path: "/dashboard/announcements", icon: RiNotification2Line },
    ],

    // ========================
    // SUPPORT SECTION
    // ========================

    // Support > Help desk
    "/dashboard/help-desk": [
        { label: "Support", path: "/dashboard/help-desk", icon: FaExclamation },
        { label: "Help desk", path: "/dashboard/help-desk", icon: IoIosHelpCircleOutline },
    ],

    // Support > States
    "/dashboard/states": [
        { label: "Support", path: "/dashboard/states", icon: FaExclamation },
        { label: "States", path: "/dashboard/states", icon: CiGlobe },
    ],

    // ========================
    // MANAGEMENT SECTION
    // ========================

    // Management > Admins
    "/dashboard/admins": [
        { label: "Management", path: "/dashboard/admins", icon: RiAdminFill },
        { label: "Admins", path: "/dashboard/admins", icon: RiAdminFill },
    ],
    // Management > Batch Configs
    "/dashboard/batch-configs": [
        { label: "Management", path: "/dashboard/batch-configs", icon: RiAdminFill },
        { label: "Batch Configs", path: "/dashboard/batch-configs", icon: RiAdminFill },
    ],

    // Management > Promo codes
    "/dashboard/promo-codes": [
        { label: "Management", path: "/dashboard/promo-codes", icon: RiAdminFill },
        { label: "Promo codes", path: "/dashboard/promo-codes", icon: MdDiscount },
    ],

    // Management > Subscription plans
    "/dashboard/subscription-plans": [
        { label: "Management", path: "/dashboard/subscription-plans", icon: RiAdminFill },
        { label: "Subscription plans", path: "/dashboard/subscription-plans", icon: LuWalletCards },
    ],

    // Management > Offers
    "/dashboard/offers": [
        { label: "Management", path: "/dashboard/offers", icon: RiAdminFill },
        { label: "Offers", path: "/dashboard/offers", icon: BiSolidOffer },
    ],

    // Management > Settings
    "/dashboard/accountSettings": [
        { label: "Management", path: "/dashboard/accountSettings", icon: RiAdminFill },
        { label: "Settings", path: "/dashboard/accountSettings", icon: CiSettings },
    ],
};